/**
 * Highlights service API calls.
 *
 * This module provides functions for highlight management including
 * creating, retrieving, and deleting text highlights within articles.
 */

import apiClient from './api';

/**
 * Create a new highlight for an article
 * @param {number} articleId - Article ID
 * @param {Object} highlightData - Highlight creation data
 * @param {Object} highlightData.annotation - W3C Web Annotation structure
 * @param {string} highlightData.exact_text - Exact highlighted text
 * @param {string} highlightData.summary_version - SHA-256 hash of summary version
 * @returns {Promise<Object>} Created highlight data
 */
export const createHighlight = async (articleId, highlightData) => {
  try {
    const response = await apiClient.post(
      `/api/articles/${articleId}/highlights`,
      highlightData
    );
    return response.data;
  } catch (error) {
    const detail = error.response?.data?.detail;
    const message = typeof detail === 'string'
      ? detail
      : Array.isArray(detail)
        ? detail.map(d => d.msg || d.message || JSON.stringify(d)).join('; ')
        : 'Failed to create highlight';
    throw new Error(message);
  }
};

/**
 * Get all highlights for a specific article
 * @param {number} articleId - Article ID
 * @param {boolean} includeDeleted - Whether to include soft-deleted highlights
 * @param {string} type - Filter by highlight type: 'all', 'article', or 'digest'
 * @returns {Promise<Object>} Highlights list response with metadata
 */
export const getArticleHighlights = async (articleId, includeDeleted = false, type = 'all') => {
  try {
    const queryParams = new URLSearchParams({
      include_deleted: includeDeleted.toString(),
      type: type,
    });

    const response = await apiClient.get(
      `/api/articles/${articleId}/highlights?${queryParams}`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch highlights'
    );
  }
};

/**
 * Get all highlights for the authenticated user, grouped by article (paginated)
 * @param {Object} options - Query options
 * @param {number} [options.page=1] - Page number (1-indexed)
 * @param {number} [options.per_page=10] - Article groups per page
 * @param {boolean} [options.includeDeleted=false] - Whether to include soft-deleted highlights
 * @returns {Promise<Object>} Grouped highlights response with pagination metadata
 */
export const getAllHighlights = async ({ page = 1, per_page = 10, includeDeleted = false } = {}) => {
  try {
    const queryParams = new URLSearchParams({
      include_deleted: includeDeleted.toString(),
      page: page.toString(),
      per_page: per_page.toString(),
    });

    const response = await apiClient.get(`/api/highlights?${queryParams}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch all highlights'
    );
  }
};

/**
 * Delete a highlight (soft delete)
 * @param {number} highlightId - Highlight ID
 * @returns {Promise<void>}
 */
export const deleteHighlight = async (highlightId) => {
  try {
    await apiClient.delete(`/api/highlights/${highlightId}`);
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to delete highlight'
    );
  }
};

/**
 * Check for detached highlights after summary changes
 * @param {number} articleId - Article ID
 * @returns {Promise<Object>} Detachment information
 */
export const checkHighlightDetachment = async (articleId) => {
  try {
    const response = await apiClient.get(
      `/api/articles/${articleId}/highlights/detachment-info`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to check highlight detachment'
    );
  }
};

/**
 * Re-attach detached highlights after summary changes
 * @param {number} articleId - Article ID
 * @param {number} similarityThreshold - Minimum similarity threshold (0.0-1.0)
 * @returns {Promise<Object>} Re-attachment results
 */
export const reattachHighlights = async (articleId, similarityThreshold = 0.8) => {
  try {
    const response = await apiClient.post(
      `/api/articles/${articleId}/highlights/reattach`,
      { similarity_threshold: similarityThreshold }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to re-attach highlights'
    );
  }
};

/**
 * Delete all highlights for an article
 * @param {number} articleId - Article ID
 * @returns {Promise<Object>} Deletion results
 */
export const deleteAllHighlights = async (articleId) => {
  try {
    const response = await apiClient.delete(
      `/api/articles/${articleId}/highlights`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to delete all highlights'
    );
  }
};

/**
 * Create a new digest highlight from topic digest
 * @param {Object} digestData - Digest highlight creation data
 * @param {number|null} digestData.article_id - Article ID if associated with article, null for general
 * @param {string} digestData.topic_name - Topic name (survives daily re-clustering)
 * @param {string} digestData.digest_section - Section: 'whats_new', 'core_theme', 'recommended_reads'
 * @param {string} digestData.digest_text - Highlighted text from digest
 * @param {string} digestData.digest_version - ISO timestamp or hash of digest generation
 * @returns {Promise<Object>} Created digest highlight data
 */
export const createDigestHighlight = async (digestData) => {
  try {
    const response = await apiClient.post(
      '/api/digest-highlights',
      digestData
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to create digest highlight'
    );
  }
};

/**
 * Get all digest highlights for a specific topic
 * @param {string} topicName - Topic name
 * @param {boolean} includeDeleted - Whether to include soft-deleted highlights
 * @returns {Promise<Object>} Digest highlights list response
 */
export const getDigestHighlightsByTopic = async (topicName, includeDeleted = false) => {
  try {
    const queryParams = new URLSearchParams({
      include_deleted: includeDeleted.toString(),
    });

    const encodedTopicName = encodeURIComponent(topicName);
    const response = await apiClient.get(
      `/api/highlights/topics/${encodedTopicName}?${queryParams}`
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch digest highlights for topic'
    );
  }
};

/**
 * Get all digest highlights for the authenticated user, grouped by topic (paginated)
 * @param {Object} options - Query options
 * @param {number} [options.page=1] - Page number (1-indexed)
 * @param {number} [options.per_page=10] - Topic groups per page
 * @param {boolean} [options.includeDeleted=false] - Whether to include soft-deleted highlights
 * @returns {Promise<Object>} Digest highlights response with pagination metadata
 */
export const getAllDigestHighlights = async ({ page = 1, per_page = 10, includeDeleted = false } = {}) => {
  try {
    const queryParams = new URLSearchParams({
      include_deleted: includeDeleted.toString(),
      page: page.toString(),
      per_page: per_page.toString(),
    });

    const response = await apiClient.get(`/api/highlights/digest?${queryParams}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch all digest highlights'
    );
  }
};
